<template>
  <div class="container mx-auto px-4 py-4">
    <PageHeading class="mb-6" :title="pageTitle" :description="pageSubtitle" :crumbs="eventCrumbs">
      <template #actions>
        <PagePrimaryButton @click="openEventTypeModal">
          <PlusIcon class="h-4 w-4 shrink-0" />
          Create New Event
        </PagePrimaryButton>
      </template>
    </PageHeading>

    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="events.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No events found. Create your first event to get started!
          </p>
        </div>

        <div v-else-if="filteredEvents.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No {{ getEventTypeLabel(activeTypeFilter).toLowerCase() }} events found.
          </p>
        </div>

        <div v-else>
          <DataTable
            :value="filteredEvents"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 rounded-lg' }
            }"
          >
            <Column header="" :exportable="false" style="width: 60px">
              <template #body="{ data }">
                <button
                  class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title="Show QR Code"
                  @click="openQRModal(data)"
                >
                  <i class="pi pi-qrcode text-xl"></i>
                </button>
              </template>
            </Column>

            <Column field="name" header="Name" sortable>
              <template #body="{ data }">
                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ data.name }}</div>
              </template>
            </Column>

            <Column field="eventType" header="Format" sortable>
              <template #body="{ data }">
                <span
                  :class="getEventTypeBadgeClass(data.eventType || EVENT_TYPES.CONCERT)"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ getEventTypeLabel(data.eventType || EVENT_TYPES.CONCERT) }}
                </span>
              </template>
            </Column>

            <Column field="isDisabled" header="Status" sortable>
              <template #body="{ data }">
                <span
                  :class="getEventStatusClass(data)"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ getEventStatus(data) }}
                </span>
              </template>
            </Column>

            <Column field="votingStartTime" header="Voting Start" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ formatDateTime(data.votingStartTime) }}
                </div>
              </template>
            </Column>

            <Column header="Actions" :exportable="false" style="width: 100px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit event"
                    data-testid="edit-event"
                    @click="openEventForm(data)"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <button
                    :disabled="deletingEventId === data.id"
                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="deletingEventId === data.id ? 'Deleting...' : 'Delete event'"
                    @click="handleDeleteEvent(data)"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Event Type Picker Modal -->
    <EventTypePickerModal
      :visible="showEventTypeModal"
      @select="onEventTypeSelected"
      @close="closeEventTypeModal"
    />

    <!-- QR Code Modal -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeQRModal"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="mb-4">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Event QR Code</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ selectedEventForQR?.name || "" }}
            </p>
          </div>

          <div class="flex flex-col items-center justify-center py-6">
            <div v-if="qrCodeDataUrl" class="mb-4">
              <img
                :src="qrCodeDataUrl"
                alt="QR Code"
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900"
              />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="min-height: 200px">
              <i class="pi pi-spin pi-spinner text-3xl text-gray-500 dark:text-gray-400"></i>
            </div>

            <div class="mt-4 text-center w-full">
              <!-- Event Code Display -->
              <div
                v-if="selectedEventForQR?.eventCode"
                class="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <p class="text-xs text-gray-600 dark:text-gray-300 mb-1">Event Code:</p>
                <p
                  class="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-wider"
                >
                  {{ selectedEventForQR.eventCode }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Share this code for quick access
                </p>
              </div>
              <div
                v-else
                class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
              >
                <p class="text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                  No event code generated
                </p>
                <Button
                  label="Generate Code"
                  severity="warning"
                  size="small"
                  :loading="generatingCode"
                  icon="pi pi-key"
                  @click="generateEventCode"
                />
              </div>

              <!-- Event URL Display -->
              <div class="mt-4">
                <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">Event URL:</p>
                <p
                  v-if="selectedEventForQR?.slug"
                  class="text-sm font-mono font-medium text-gray-900 dark:text-white break-all px-2"
                >
                  {{ getEventUrl(selectedEventForQR) }}
                </p>
                <p v-else class="text-sm text-orange-600 dark:text-orange-400 px-2">
                  This event does not have a slug. Please add a slug to generate a QR code.
                </p>
                <button
                  v-if="selectedEventForQR?.slug"
                  class="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200"
                  :class="
                    urlCopied
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  "
                  @click="copyEventUrl"
                >
                  <i :class="urlCopied ? 'pi pi-check' : 'pi pi-copy'" class="text-xs"></i>
                  {{ urlCopied ? "Copied!" : "Copy URL" }}
                </button>
              </div>
            </div>
          </div>

          <div
            class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700"
          >
            <button type="button" class="btn btn-secondary" @click="closeQRModal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete Event
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete
            <strong class="dark:text-white">{{ eventToDelete?.name }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <button class="btn btn-secondary flex-1" @click="cancelDelete">Cancel</button>
            <button
              :disabled="deletingEventId !== null"
              class="btn btn-danger flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmDelete"
            >
              {{ deletingEventId !== null ? "Deleting..." : "Delete" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/vue/24/outline";
import PageHeading from "@/components/common/PageHeading.vue";
import PagePrimaryButton from "@/components/common/PagePrimaryButton.vue";
import QRCode from "qrcode";
import EventTypePickerModal from "@/components/admin/EventTypePickerModal.vue";
import { formatDateTime } from "@/utils/dateFormat";
import { EVENT_TYPES, getEventTypeBadgeClass, getEventTypeLabel } from "@/constants/eventTypes";
import { DEFAULT_LOCALE } from "@/constants/locales";
import api from "../../services/api";

defineOptions({ name: "AdminEvents" });

const toast = useToast();
const route = useRoute();
const router = useRouter();

const events = ref([]);
const loading = ref(true);
const error = ref("");
const showEventTypeModal = ref(false);
const showDeleteModal = ref(false);
const eventToDelete = ref(null);
const deletingEventId = ref(null);
const rows = ref(10);
const showQRModal = ref(false);
const selectedEventForQR = ref(null);
const qrCodeDataUrl = ref("");
const generatingCode = ref(false);
const urlCopied = ref(false);
let copyTimeout = null;

const activeTypeFilter = computed(() => {
  const type = route.query.type;
  if (typeof type === "string" && Object.values(EVENT_TYPES).includes(type)) {
    return type;
  }
  return null;
});

const filteredEvents = computed(() => {
  if (!activeTypeFilter.value) return events.value;
  return events.value.filter(
    (event) => (event.eventType || EVENT_TYPES.CONCERT) === activeTypeFilter.value
  );
});

const pageTitle = computed(() => {
  if (activeTypeFilter.value) {
    return getEventTypeLabel(activeTypeFilter.value);
  }
  return "Events";
});

const eventCrumbs = computed(() => {
  if (!activeTypeFilter.value) {
    return [{ label: "Events" }, { label: "All Events" }];
  }
  return [{ label: "Events", to: "/events" }, { label: getEventTypeLabel(activeTypeFilter.value) }];
});

const pageSubtitle = computed(() => {
  if (activeTypeFilter.value) {
    return `${filteredEvents.value.length} of ${events.value.length} events`;
  }
  return `Total: ${events.value.length} events`;
});

const fetchEvents = async () => {
  try {
    loading.value = true;
    error.value = "";
    const response = await api.get("/api/events");
    if (response.data.success) {
      events.value = response.data.data.events || [];
    } else {
      error.value = response.data.message || "Failed to fetch events";
    }
  } catch (err) {
    console.error("Failed to fetch events:", err);
    error.value = err.response?.data?.message || "Failed to fetch events";
  } finally {
    loading.value = false;
  }
};

const openEventTypeModal = () => {
  showEventTypeModal.value = true;
};

const closeEventTypeModal = () => {
  showEventTypeModal.value = false;
};

const onEventTypeSelected = (eventType) => {
  closeEventTypeModal();
  router.push({ path: "/events/new", query: { type: eventType } });
};

const openEventForm = (event) => {
  router.push(`/events/${event.id}/edit`);
};

const handleDeleteEvent = (event) => {
  eventToDelete.value = event;
  showDeleteModal.value = true;
};

const cancelDelete = () => {
  showDeleteModal.value = false;
  eventToDelete.value = null;
};

const confirmDelete = async () => {
  if (!eventToDelete.value) return;

  try {
    deletingEventId.value = eventToDelete.value.id;
    error.value = "";

    const response = await api.delete(`/api/events/${deletingEventId.value}`);

    if (response.data.success) {
      events.value = events.value.filter((e) => e.id !== deletingEventId.value);
      showDeleteModal.value = false;
      eventToDelete.value = null;
    } else {
      error.value = response.data.message || "Failed to delete event";
    }
  } catch (err) {
    console.error("Failed to delete event:", err);
    error.value = err.response?.data?.message || "Failed to delete event";
  } finally {
    deletingEventId.value = null;
  }
};

const getEventStatus = (event) => {
  const now = new Date();
  const startTime = new Date(event.votingStartTime);
  const endTime = event.votingEndTime ? new Date(event.votingEndTime) : null;

  if (event.isDisabled) return "Disabled";
  if (now < startTime) return "Upcoming";
  if (endTime && now > endTime) return "Ended";
  return "Active";
};

const openQRModal = async (event) => {
  selectedEventForQR.value = event;
  showQRModal.value = true;
  qrCodeDataUrl.value = "";

  try {
    // Generate QR code with event URL using concerts service URL + event slug
    // The concerts service is a separate service handling voting
    const concertsUrl = import.meta.env.VITE_CONCERTS_URL || window.location.origin;

    // QR code must use the event slug
    if (!event.slug) {
      console.warn("Event does not have a slug. Please set a slug to generate QR code.");
      qrCodeDataUrl.value = "";
      return;
    }

    const eventUrl = `${concertsUrl}/${event.slug}?lang=${event.defaultLocale || DEFAULT_LOCALE}`;

    const dataUrl = await QRCode.toDataURL(eventUrl, {
      width: 300,
      margin: 2,
      color: {
        dark: "#000000",
        light: "#FFFFFF"
      }
    });
    qrCodeDataUrl.value = dataUrl;
  } catch (err) {
    console.error("Failed to generate QR code:", err);
  }
};

const generateEventCode = async () => {
  if (!selectedEventForQR.value) return;

  generatingCode.value = true;
  try {
    // Update the event to trigger code generation (backend generates code if missing)
    const response = await api.put(`/api/events/${selectedEventForQR.value.id}`, {
      name: selectedEventForQR.value.name,
      slug: selectedEventForQR.value.slug,
      date: selectedEventForQR.value.date,
      // Include other fields to ensure update works
      isPrivate: selectedEventForQR.value.isPrivate,
      location: selectedEventForQR.value.location,
      votingStartTime: selectedEventForQR.value.votingStartTime,
      votingEndTime: selectedEventForQR.value.votingEndTime
    });

    if (response.data.success) {
      // Update the selected event with the new code
      selectedEventForQR.value.eventCode = response.data.data.event.eventCode;
      // Refresh events list to get updated code
      await fetchEvents();
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Event code generated successfully",
        life: 3000
      });
    }
  } catch (err) {
    console.error("Failed to generate event code:", err);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: err.response?.data?.message || "Failed to generate event code",
      life: 5000
    });
  } finally {
    generatingCode.value = false;
  }
};

const closeQRModal = () => {
  showQRModal.value = false;
  selectedEventForQR.value = null;
  qrCodeDataUrl.value = "";
};

const getEventUrl = (event) => {
  if (!event) return "";
  if (!event.slug) return "Event slug required for URL";
  const concertsUrl = import.meta.env.VITE_CONCERTS_URL || window.location.origin;
  return `${concertsUrl}/${event.slug}?lang=${event.defaultLocale || DEFAULT_LOCALE}`;
};

const copyEventUrl = async () => {
  const url = getEventUrl(selectedEventForQR.value);
  if (!url || url.includes("required")) return;
  try {
    await navigator.clipboard.writeText(url);
    urlCopied.value = true;
    clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      urlCopied.value = false;
    }, 2000);
  } catch (err) {
    console.error("Failed to copy URL:", err);
  }
};

const getEventStatusClass = (event) => {
  const status = getEventStatus(event);
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800";
    case "Upcoming":
      return "bg-blue-100 text-blue-800";
    case "Ended":
      return "bg-gray-100 text-gray-800";
    case "Disabled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

onMounted(() => {
  fetchEvents();
});
</script>
